import { useTranslation } from "react-i18next";
import { useUIStore } from "../stores/uiStore";
import { useSiteStore } from "../stores/siteStore";
import { useLiveDataAvailability } from "../hooks/useLiveDataAvailability";
import { useCollapsedPanel } from "../hooks/useCollapsedPanel";
import { Reveal } from "../components/Reveal";
import { isActiveSearch, type ProductFilters } from "../scene/productSearch";
import panelCss from "./Panel.module.css";
import css from "./FinderPanel.module.css";

/**
 * ADR 203: the /live finder — a dedicated panel replacing the ADR 196 top-bar
 * box. Text (item code, scanned label, WMS description) plus the WMS-attribute
 * predicates the Softys position report feeds: dwell (days since entry),
 * expiry and manufacturing dates. Predicates AND together; matching itself
 * stays with the cells' owner (OccupancyLayerContainer) — this panel only
 * holds what was typed and reads the hit count back from the store.
 */
export function FinderPanel() {
  const { t } = useTranslation();
  const query = useUIStore((s) => s.productQuery);
  const setQuery = useUIStore((s) => s.setProductQuery);
  const filters = useUIStore((s) => s.productFilters);
  const setFilters = useUIStore((s) => s.setProductFilters);
  const clearAll = useUIStore((s) => s.clearProductSearch);
  const count = useUIStore((s) => s.productMatchCount);
  const occupancyOn = useUIStore((s) => s.layers.occupancy);
  const toggleLayer = useUIStore((s) => s.toggleLayer);
  const hasInventory = useSiteStore((s) => s.hasCapability("inventory"));
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  const activeWarehouseId = useSiteStore((s) => s.activeWarehouseId);
  const available = useLiveDataAvailability(activeSiteId, activeWarehouseId);
  const [collapsed, toggleCollapsed] = useCollapsedPanel("finder");
  // ADR 227: capability AND data, like every other run-fed control. Everything this
  // panel filters comes out of a counted run — with none imported, the search box is
  // a claim the app cannot keep (the gap the webinar import surfaced).
  // ADR 356: the gate speaks its requirements under Modo diagnóstico.
  if (!hasInventory || !available.runs) {
    return <Reveal label={t("live.search.title")} reqs={[
      { label: t("req.inventoryCapability"), ok: hasInventory },
      { label: t("req.runs"), ok: !!available.runs },
    ]} />;
  }

  // Searching means wanting to SEE the goods — turn the layer on for them.
  const showGoods = () => { if (!occupancyOn) toggleLayer("occupancy"); };
  const patch = (p: Partial<ProductFilters>) => { setFilters(p); showGoods(); };

  const active = isActiveSearch(query, filters);
  return (
    <section className={`${panelCss.panel} ${css.finder}`} aria-label={t("live.search.title")}>
      <h2 className={`${panelCss.title} ${collapsed ? panelCss.titleCollapsed : ""}`}>
        <button type="button" className={panelCss.titleBtn} aria-expanded={!collapsed} onClick={toggleCollapsed}>
          {t("live.search.title")}
          <span className={panelCss.caret} aria-hidden>{collapsed ? "▸" : "▾"}</span>
        </button>
      </h2>
      {!collapsed && (<>
      <input
        className={css.query}
        value={query}
        placeholder={t("live.search.placeholder")}
        aria-label={t("live.search.placeholder")}
        onChange={(e) => { setQuery(e.target.value); showGoods(); }}
        onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
      />
      <div className={css.row}>
        <span className={css.rowLabel}>{t("live.search.dwell")}</span>
        <select
          className={css.op}
          value={filters.dwellOp}
          aria-label={t("live.search.dwellOpLabel")}
          onChange={(e) => patch({ dwellOp: e.target.value as ProductFilters["dwellOp"] })}
        >
          <option value="gte">≥</option>
          <option value="lte">≤</option>
        </select>
        <input
          className={css.days}
          type="number"
          min={0}
          value={filters.dwellDays}
          aria-label={t("live.search.dwellLabel")}
          onChange={(e) => patch({ dwellDays: e.target.value })}
        />
        <span className={css.unit}>{t("live.search.days")}</span>
      </div>
      <div className={css.row}>
        <span className={css.rowLabel}>{t("live.search.expiry")}</span>
        <select
          className={css.op}
          value={filters.expiryOp}
          aria-label={t("live.search.expiryOpLabel")}
          onChange={(e) => patch({ expiryOp: e.target.value as ProductFilters["expiryOp"] })}
        >
          <option value="lte">{t("live.search.until")}</option>
          <option value="gte">{t("live.search.after")}</option>
        </select>
        <input
          className={css.date}
          type="date"
          value={filters.expiryDate}
          aria-label={t("live.search.expiryLabel")}
          onChange={(e) => patch({ expiryDate: e.target.value })}
        />
      </div>
      <div className={css.row}>
        <span className={css.rowLabel}>{t("live.search.mfg")}</span>
        <select
          className={css.op}
          value={filters.mfgOp}
          aria-label={t("live.search.mfgOpLabel")}
          onChange={(e) => patch({ mfgOp: e.target.value as ProductFilters["mfgOp"] })}
        >
          <option value="lte">{t("live.search.until")}</option>
          <option value="gte">{t("live.search.after")}</option>
        </select>
        <input
          className={css.date}
          type="date"
          value={filters.mfgDate}
          aria-label={t("live.search.mfgLabel")}
          onChange={(e) => patch({ mfgDate: e.target.value })}
        />
      </div>
      {active && (
        <div className={css.footer}>
          {count != null && (
            <span className={count === 0 ? css.countNone : css.count} data-testid="product-search-count">
              {count === 0 ? t("live.search.none") : t("live.search.matches", { count })}
            </span>
          )}
          <button type="button" className={css.clear} onClick={clearAll}>
            {t("live.search.clear")}
          </button>
        </div>
      )}
      </>)}
    </section>
  );
}
